import { useFocusEffect } from '@react-navigation/native';
import {
    useCallback,
    useState
} from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
    Button,
    Page,
    Text,
    View
} from '../../../common';
import { Log } from '../../../common/Log';
import { useUserInfo } from '../../../hooks/useUserInfo';
import { COLORS } from '../../../styles/colors';
import { consoleLog } from '../../../utils/log';
import { BackupListForSelect } from '../Backup/BackupList';
import {
    getICloudBackupInfo,
    removeBackup,
    renameBackupToDevice
} from '../Backup/icloud/icloud';
import {
    getDevicesData,
    setDeviceData
} from '../Backup/icloud/iCloudDevices';
import { getMetadata } from '../Backup/icloud/iCloudMetadata';
import { DeviceEditForm } from './DeviceEdit';
import { DevicesList } from './DevicesList';

const getRandomUuidV4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const IOSBackupRename = Page.register(() => {

    const [ backupList, setBackupList ] = useState([]);
    const [ selectedBackup, setSelectedBackup ] = useState();
    const [ devicesList, setDevicesList ] = useState([]);
    const [ metadataObject, setMetadataObject ] = useState({});
    const [ error, setError ] = useState('');
    const [ selectedDevice, setSelectedDevice ] = useState();
    const info = useUserInfo();
    const { me: { profileId } = {} } = info ?? {};
    const safe = useSafeAreaInsets();

    const reloadDevices = useCallback(() => {
        getDevicesData()
            .then(d => {
                setDevicesList(Array.from(Object.values(d)));
            })
            .catch(setError);
    }, []);

    const reloadBackups = useCallback(() => {
        getICloudBackupInfo(profileId)
            .then(b => {
                consoleLog('getICloudBackupInfo', b);
                setBackupList(b);
            })
            .catch(setError);
    }, [ profileId ]);

    const reloadMetadata = useCallback(() => {
        getMetadata({ profileId })
            .then(setMetadataObject)
            .catch(setError);
    }, [ profileId ]);

    const addDeviceHandler = useCallback(async ({
        deviceName,
        deviceModel,
        deviceId,
    } = {}) => {
        const newDevice = {};
        newDevice.deviceName = deviceName ?? 'New name';
        newDevice.deviceModel = deviceModel ?? 'Model';
        newDevice.deviceId = deviceId ?? getRandomUuidV4();
        await setDeviceData(newDevice);
        reloadDevices();
        if (deviceId) {
            setSelectedDevice(null);
        }
    }, [ reloadDevices ]);

    const removeBackupHandler = useCallback(() => {
        removeBackup({
            backup: selectedBackup,
            profileId,
        });
    }, [ reloadBackups,
        reloadMetadata,
        profileId,
        selectedBackup ]);

    const renameBackupHandler = useCallback(() => {
        renameBackupToDevice({
            file: selectedBackup?.file?.file,
            profileId,
            deviceId: selectedDevice?.deviceId,
        })
            .then(() => {
                reloadBackups();
                reloadMetadata();
            })
            .catch((e) => {
                setError(e);
            });
    }, [ selectedBackup?.file?.file,
        profileId,
        selectedDevice?.deviceId,
        reloadBackups,
        reloadMetadata ]);


    useFocusEffect(useCallback(() => {
        if (!profileId) {
            return;
        }
        reloadBackups();
        reloadDevices();
        reloadMetadata();
    }, [ profileId,
        reloadBackups,
        reloadDevices,
        reloadMetadata ]));
    return (<Page
        noscroll
        safeArea={safe}
        footer={<View style={{
            padding: 16,
        }}>
            <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
            }}>
                <Button
                    primary
                    title={'Add device'}
                    action={addDeviceHandler}/>
                <Button
                    primary
                    title={'Rename backup'}
                    disabled={!selectedDevice || !selectedBackup}
                    action={renameBackupHandler}
                />
                <Button
                    danger
                    title={'Del backup'}
                    disabled={!selectedBackup}
                    action={removeBackupHandler}
                />
            </View>
        </View>}>
        <Log
            value={{
                error,
                devicesList,
                profileId,
                metadataObject,
                backupList,
                selectedDevice,
                selectedBackup,
            }}
            display={false}
            text={'Values'}/>
        <View style={{
            borderColor: 'red',
            borderWidth: 0,
            flexDirection: 'column',
        }}>
            <Text style={{ color: COLORS.ERROR }}>{JSON.stringify(error)}</Text>
            <DevicesList
                data={devicesList}
                selected={selectedDevice}
                onSelect={setSelectedDevice}/>
            {selectedDevice?.deviceId && <View>
                <DeviceEditForm
                    data={selectedDevice}
                    onSave={addDeviceHandler}
                    onCancel={() => setSelectedDevice(null)}
                />
            </View>}
            <View style={{
                flexDirection: 'column',
                borderWidth: 0,
                borderColor: 'blue',
            }}>
                <Text>Список бэкапов</Text>
                <BackupListForSelect
                    isIos
                    data={backupList?.backups}
                    selected={selectedBackup}
                    onSelect={setSelectedBackup}/>
            </View>
        </View>
    </Page>);
});
